import React, { useState, useEffect, useCallback, useRef } from "react";
import NuSingleSelect from "js/components/NuSingleSelect";
import Api from "js/api/authorisedApi";
import moment from "moment";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import { useClassroomPolicyStore } from "stores/v2/classroomPolicyStore";
import { Flex, Spinner, ModalBody, ModalFooter } from "@chakra-ui/react";
import { ClasswizeModal } from "../../../components/ui/Modal";

interface BypassModalProps {
	onClose: () => void;
	open: boolean;
}

interface BypassCodeRequest {
	code: string;
	creation_time: number;
	expiry_time: number;
	id?: number;
}

interface GetBypassCodeResponse {
	result: {
		code: string;
		created_by: string;
		creation_time: number;
		expiry_time: number;
		id: number;
	}[];
}

interface GenerateBypassCodeResponse {
	result: string;
}

interface BypassDuration {
	value: string;
	text: string;
}

const BypassModal: React.FC<BypassModalProps> = ({ onClose, open }) => {
	const [bypassActiveUntil, setBypassActiveUntil] = useState<number>(0);
	const [creationTime, setCreationTime] = useState<number>(0);
	const [timeframe, setTimeframe] = useState<string>("600");
	const [bypassActive, setBypassActive] = useState<boolean>(false);
	const [loadingCode, setLoadingCode] = useState<boolean>(false);
	const [stoppingCode, setStoppingCode] = useState<boolean>(false);
	const [addingCode, setAddingCode] = useState<boolean>(false);
	const [code, setCode] = useState<string>("");
	const [id, setId] = useState<number | undefined>(undefined);

	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const hasInitialized = useRef<boolean>(false);

	const generateCode = useCallback(() => {
		setLoadingCode(true);
		Api.get(
			"/backend/classroom/bypass_code/generate",
			(result: GenerateBypassCodeResponse) => {
				if (result["result"]) {
					setTimeout(() => {
						setCode(result["result"]);
						setLoadingCode(false);
					}, 0);
				}
			},
			() => {
				setLoadingCode(false);
			}
		);
	}, []);

	const getBypassCode = useCallback(() => {
		const now = moment().unix();
		setLoadingCode(true);

		Api.get("/backend/classroom/bypass_code", (result: GetBypassCodeResponse) => {
			const last = result["result"][0];

			if (last && last.expiry_time >= now) {
				setCode(last.code);
				setBypassActiveUntil(last.expiry_time);
				setCreationTime(last.creation_time);
				setId(last.id);
				setBypassActive(true); // Set bypass as active when there's a valid existing code
			} else {
				generateCode();
			}
			setLoadingCode(false);
		});
	}, [generateCode]);

	const reset = useCallback(() => {
		setBypassActiveUntil(0);
		setCreationTime(0);
		setTimeframe("600");
		setCode("");
		setBypassActive(false); // Reset bypass active state
		setStoppingCode(false);
	}, []);

	//! event is undefined if this function is trigerred by enter key listener in Dialog
	const startBypass = useCallback(() => {
		setAddingCode(true);

		const activeUntil = moment().unix() + parseInt(timeframe);
		const creationTime = moment().unix();

		const bypass: BypassCodeRequest = {
			code: code,
			creation_time: creationTime,
			expiry_time: activeUntil,
		};

		Api.post(
			"/backend/classroom/bypass_code",
			bypass,
			() => {
				setBypassActiveUntil(activeUntil);
				setCreationTime(creationTime);
				setAddingCode(false);

				useClassroomPolicyStore.getState().setBypassActiveUntil(activeUntil);

				getBypassCode();
				onClose();
			},
			() => {
				setAddingCode(false);
			}
		);
	}, [timeframe, code, getBypassCode, onClose]);

	//! event is undefined if this function is trigerred by enter key listener in Dialog
	const stopBypass = useCallback(() => {
		setStoppingCode(true);

		const now = moment().unix();

		const bypass: BypassCodeRequest = {
			code: code,
			creation_time: creationTime,
			expiry_time: now,
			id: id,
		};

		Api.post("/backend/classroom/bypass_code/update", bypass, () => {
			useClassroomPolicyStore.getState().setBypassActiveUntil(now);
			reset();
			onClose();
		});
	}, [code, creationTime, id, reset, onClose]);

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const prepareBypassDuration = useCallback(
		(): BypassDuration[] => [
			{ value: "600", text: "10 Minutes" },
			{ value: "900", text: "15 Minutes" },
			{ value: "1200", text: "20 Minutes" },
			{ value: "1800", text: "30 Minutes" },
			{ value: "2700", text: "45 Minutes" },
			{ value: "3600", text: "1 Hour" },
			{ value: "7200", text: "2 Hours" },
			{ value: "10800", text: "3 Hours" },
		],
		[]
	);

	const changeBypassDuration = useCallback((value: string) => {
		setTimeframe(value);
	}, []);

	const currentBypassHandler = useCallback(() => {
		return bypassActive ? stopBypass : startBypass;
	}, [bypassActive, stopBypass, startBypass]);

	const bypassTimer = useCallback(() => {
		const bypassUnixTimestamp = bypassActiveUntil > moment().unix();
		const end = moment.utc(bypassActiveUntil, "X");
		const timeLeft = moment.utc(end.diff(moment.utc()));

		if (bypassUnixTimestamp !== bypassActive) {
			setBypassActive(bypassUnixTimestamp);
		}
		const bypassTTL = document.getElementById("bypassTimeLeft");
		if (bypassTTL) {
			bypassTTL.innerText = timeLeft.format("HH:mm:ss");
		}
	}, [bypassActiveUntil, bypassActive]);

	const renderBypassButton = useCallback(() => {
		let stopLabel = "Stop";
		let startLabel = "Start";
		let disabled = false;

		if (stoppingCode) {
			stopLabel = "Stopping Bypass";
			disabled = true;
		} else if (addingCode) {
			startLabel = "Starting Bypass";
			disabled = true;
		}

		if (bypassActive) {
			return (
				<PrimaryButton
					className="mui-flat-button"
					data-testid="stop-bypass-button"
					label={stopLabel}
					onClick={currentBypassHandler()}
					disabled={disabled}
				/>
			);
		} else {
			return (
				<PrimaryButton
					className="mui-flat-button"
					data-testid="start-bypass-button"
					label={startLabel}
					onClick={currentBypassHandler()}
					disabled={disabled}
				/>
			);
		}
	}, [stoppingCode, addingCode, bypassActive, currentBypassHandler]);

	const renderButtons = useCallback(() => {
		return <React.Fragment>{renderBypassButton()}</React.Fragment>;
	}, [renderBypassButton]);

	const renderTimeSelection = useCallback(() => {
		if (bypassActive) {
			return (
				<div>
					<div className="BypassModal_title">Time Left</div>
					<div className="BypassModal_time">
						<span id={"bypassTimeLeft"} /> ({moment(bypassActiveUntil, "X").format("LLL")})
					</div>
				</div>
			);
		} else {
			return (
				<NuSingleSelect
					allItems={prepareBypassDuration()}
					selectedItem={timeframe}
					searchPlaceholder="Bypass Duration"
					title="Bypass Duration"
					onChange={changeBypassDuration}
				/>
			);
		}
	}, [bypassActive, bypassActiveUntil, prepareBypassDuration, timeframe, changeBypassDuration]);

	const renderCode = useCallback(() => {
		return (
			<div>
				<div className="BypassModal_title">Bypass Code</div>
				<div className="BypassModal_code">{code ? code : "----"}</div>
			</div>
		);
	}, [code]);

	// Initialize bypass code only once when modal opens
	useEffect(() => {
		if (open && !hasInitialized.current) {
			hasInitialized.current = true;
			getBypassCode();
		}
		if (!open) {
			hasInitialized.current = false;
		}
	}, [open, getBypassCode]);

	useEffect(() => {
		if (open) {
			timerRef.current = setInterval(bypassTimer, 1000);
		}
		return () => {
			if (timerRef.current && typeof clearInterval !== "undefined") {
				clearInterval(timerRef.current);
			}
		};
	}, [open, bypassTimer]);

	return (
		<ClasswizeModal title="Bypass Code" isOpen={open} onClose={close} dataTestId="bypass-modal" size="md">
			{loadingCode ? (
				<ModalBody>
					<Flex justifyContent="center" alignItems="center" h="200px">
						<Spinner size="xl" />
					</Flex>
				</ModalBody>
			) : (
				<>
					<ModalBody>
						<Flex direction="row" gap={4}>
							<div className="BypassModal_duration">{renderTimeSelection()}</div>
							<div>{renderCode()}</div>
						</Flex>
					</ModalBody>
					<ModalFooter>{renderButtons()}</ModalFooter>
				</>
			)}
		</ClasswizeModal>
	);
};

export default BypassModal;
